import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  DocumentTextIcon,
  ExclamationTriangleIcon,
  TrashIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import DocumentViewer from '../../components/DocumentViewer';
import apiClient from '../../utils/apiClient';
import { BuchungDetail } from './types';
import { euro } from './utils';

interface Props {
  buchungId: number | null;
  onClose: () => void;
  onEntfernt: () => void;
  onMessage: (type: 'info' | 'error', title: string, message: string) => void;
}

const BuchungsModal: React.FC<Props> = ({ buchungId, onClose, onEntfernt, onMessage }) => {
  const [daten, setDaten] = useState<BuchungDetail | null>(null);
  const [laedt, setLaedt] = useState(false);
  const [entfernt, setEntfernt] = useState(false);
  const [bestaetigen, setBestaetigen] = useState(false);
  const [beleg, setBeleg] = useState<{ id: number; name: string; file_path?: string } | null>(null);

  useEffect(() => {
    if (!buchungId) return;
    setLaedt(true);
    setDaten(null);
    apiClient.get(`/api/anlagen/buchung/${buchungId}`)
      .then((response) => setDaten(response.data.data))
      .catch((error) => onMessage('error', 'Buchung nicht ladbar', error.response?.data?.message || 'Unbekannter Fehler.'))
      .finally(() => setLaedt(false));
  }, [buchungId, onMessage]);

  useEffect(() => {
    if (!buchungId) return;
    const vorher = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const escape = (event: KeyboardEvent) => event.key === 'Escape' && onClose();
    window.addEventListener('keydown', escape);
    return () => {
      document.body.style.overflow = vorher;
      window.removeEventListener('keydown', escape);
    };
  }, [buchungId, onClose]);

  if (!buchungId) return null;

  const zuordnungLoesen = async () => {
    setEntfernt(true);
    try {
      await apiClient.delete(`/api/anlagen/buchung/${buchungId}/zuordnung`);
      onEntfernt();
      onClose();
      onMessage('info', 'Zuordnung aufgehoben', 'Die Buchung erscheint wieder im Prüfassistenten. Die Finanzbuchung selbst blieb unverändert.');
    } catch (error: any) {
      onMessage('error', 'Zuordnung nicht aufgehoben', error.response?.data?.message || 'Unbekannter Fehler.');
    } finally {
      setEntfernt(false);
    }
  };

  const zeile = (label: string, value: React.ReactNode) => (
    <div className="grid grid-cols-1 gap-1 border-b border-gray-100 py-3 last:border-0 dark:border-gray-700 sm:grid-cols-[180px_1fr]">
      <dt className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">{label}</dt>
      <dd className="break-words text-sm text-gray-900 dark:text-gray-100">{value}</dd>
    </div>
  );

  return createPortal(
    <div className="modal-backdrop" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <section className="modal lg" role="dialog" aria-modal="true" aria-labelledby="buchung-modal-title">
        <div className="flex items-start justify-between border-b border-gray-200 p-5 dark:border-gray-700 sm:p-6">
          <div>
            <p className="modal-kicker">Originaldaten</p>
            <h2 id="buchung-modal-title" className="mt-1 text-xl font-bold text-gray-900 dark:text-white">Buchung und Beleg prüfen</h2>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              {daten ? `${daten.datum} · ${daten.konto || 'Konto unbekannt'}` : 'Wird geladen …'}
            </p>
          </div>
          <button className="btn ghost sm !p-2" onClick={onClose} aria-label="Buchungsdetails schließen">
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="max-h-[72vh] overflow-y-auto p-5 sm:p-6">
          {laedt && <p className="py-12 text-center text-sm text-gray-500">Buchungsdaten werden geladen …</p>}
          {daten && (
            <div className="space-y-6">
              <dl>
                {zeile('Betrag', <strong>{euro(Math.abs(daten.betrag))}</strong>)}
                {zeile('Verwendungszweck', daten.verwendungszweck || '—')}
                {zeile('Buchungstext', daten.buchungstext || '—')}
                {zeile('Zahlungsbeteiligter', daten.name_zahlungsbeteiligter || '—')}
                {zeile('Kategorie', daten.kategorien.length
                  ? daten.kategorien.map((item) => `${item.name} (${euro(Math.abs(item.betrag))})`).join(', ')
                  : 'Noch nicht kategorisiert')}
              </dl>

              {daten.nicht_kategorisiert > 0 && (
                <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-950 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-100">
                  <div className="flex gap-3">
                    <ExclamationTriangleIcon className="h-5 w-5 shrink-0 text-amber-600" />
                    <p><strong>{euro(daten.nicht_kategorisiert)}</strong> sind keiner Kategorie zugeordnet. Kläre zuerst die Finanzbuchung, bevor du eine Anlage anlegst.</p>
                  </div>
                </div>
              )}

              <div>
                <h3 className="text-sm font-bold text-gray-900 dark:text-white">Verknüpfte Belege</h3>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Öffne den Beleg und prüfe, was tatsächlich gekauft oder geleistet wurde.</p>
                <div className="mt-3 flex flex-wrap gap-2">
                  {daten.belege.length ? daten.belege.map((item) => (
                    <button key={item.id} className="btn ghost sm" onClick={() => setBeleg(item)}>
                      <DocumentTextIcon className="h-4 w-4" /> {item.name}
                    </button>
                  )) : <span className="text-sm text-gray-500">Kein Beleg verknüpft</span>}
                </div>
              </div>

              {(daten.pruefung || daten.anlagen.length > 0) && (
                <div className="rounded-2xl border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-800/60">
                  <h3 className="text-sm font-bold text-gray-900 dark:text-white">Bestehende Zuordnung korrigieren</h3>
                  <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">Hebt nur die Anlagen-Zuordnung auf. Kontostand und Finanzbuchung werden nicht verändert.</p>
                  {!bestaetigen ? (
                    <button className="btn danger-ghost sm mt-3" onClick={() => setBestaetigen(true)}>
                      <TrashIcon className="h-4 w-4" /> Zuordnung aufheben
                    </button>
                  ) : (
                    <div className="mt-3 flex flex-wrap items-center gap-2">
                      <span className="text-sm font-semibold text-red-700 dark:text-red-300">Wirklich aufheben?</span>
                      <button className="btn danger sm" disabled={entfernt} onClick={zuordnungLoesen}>{entfernt ? 'Wird aufgehoben …' : 'Ja, aufheben'}</button>
                      <button className="btn ghost sm" onClick={() => setBestaetigen(false)}>Abbrechen</button>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </section>

      {beleg && (
        <DocumentViewer
          documentId={beleg.id}
          fileName={beleg.name}
          filePath={beleg.file_path || beleg.name}
          onClose={() => setBeleg(null)}
        />
      )}
    </div>,
    document.body
  );
};

export default BuchungsModal;

